import React, {FunctionComponent, useEffect, useState} from 'react';
import {Modal} from 'react-bootstrap';
import {RouteComponentProps, useHistory, useParams} from 'react-router-dom';
import {useForm} from "react-hook-form";
import {Trans, useTranslation} from 'react-i18next';

import './EditManufacturer.scss';
import {Breadcrumbs, Button, ErrorMessage, FormInput, FormSelect, Loader, Page, PageHeader} from 'components';
import {IDevice, IDeviceModel, IFirmware, IManufacturer} from 'interfaces';
import {ErrorType, LoadState} from 'enums';
import {DeviceInfoService, parseAxiosError} from 'services';
import {useCancelablePromise} from 'hooks';

type IManufacturerForm = {
    manufacturer: string;
}

export const EditManufacturer: FunctionComponent<{}> = (props) => {
    const { cancelablePromise } = useCancelablePromise();
    const form = useForm<IManufacturerForm>({mode: 'onTouched'});
    const { t, i18n } = useTranslation();
    const history = useHistory();
    let urlParams = useParams<{ id: string }>();
    const [loadState, setLoadState] = useState(LoadState.Loading);
    const [saveState, setSaveState] = useState(LoadState.Initial);
    const [deleteState, setDeleteState] = useState(LoadState.Initial);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const isAPICall = saveState === LoadState.Loading || deleteState === LoadState.Loading;

    const isNew = urlParams.id === 'new';
    let id: number;
    if (!isNew) {
        id = parseInt(urlParams.id, 10);
    }

    const mapManufacturerToForm = (m: IManufacturer): IManufacturerForm => {
        return {
            manufacturer: m.name,
        }
    }

    const mapFormToManufacturer = (formData: IManufacturerForm): IManufacturer => {
        // Note: manufacturer id will be undefined for existing firmware versions,
        // because browsers don't submit the values of disabled fields.
        return {
            id: id,
            name: formData.manufacturer,
        };
    }

    // Load the firmware versions and manufacturers
    useEffect(() => {
        let promises = [cancelablePromise(DeviceInfoService.getManufacturerMap())];
        if (!isNew) {
            promises.push(cancelablePromise(DeviceInfoService.getManufacturer(id)));
        }
        Promise.all(promises)
            .then(([deviceInfo, man]) => {
                if (!isNew) { 
                    form.reset(mapManufacturerToForm(man));
                }
                setLoadState(LoadState.Success);
            })
            .catch((err) => {
                setLoadError(parseAxiosError(err));
                setLoadState(LoadState.Error);
            });
    }, []);

    let isSubmitting = false;
    const onSubmit = (formData: IManufacturerForm) => {
        if (isSubmitting || loadState !== LoadState.Success || saveState === LoadState.Loading) return;
        // "setState" is async, so set a var immediately to prevent possible double submit.
        isSubmitting = true;
        setSaveError('');
        setSaveState(LoadState.Loading);

        const manufacturerFromForm = mapFormToManufacturer(formData);

        const p = cancelablePromise(DeviceInfoService.createManufacturer(manufacturerFromForm));
        p.then((man) => {
                setSaveState(LoadState.Success);
                // Update the form with the returned object from the server.
                form.reset(mapManufacturerToForm(man));
                history.push('/manufacturers');
            })
            .catch((err) => {
                console.log('ERROR saving: ', err);
                setSaveState(LoadState.Error);
                setSaveError(parseAxiosError(err));
            })
    }

    const onCancel = () => {
        // No need to do anything, just go back to firmware versions
        history.push('/manufacturers');
    }

    const onOpenDeleteModal = () => {
        // Clear any errors
        if (deleteState === LoadState.Error) {
            setDeleteState(LoadState.Initial);
            setDeleteError('');
        }
        setShowDeleteModal(true);
    }

    const onDelete = () => {
        if (isSubmitting) return;
        if (loadState !== LoadState.Success) return;
        if (saveState === LoadState.Loading) return;
        // "setState" is async, so set a var immediately to prevent possible double submit.
        isSubmitting = true;
        setDeleteError('');
        setDeleteState(LoadState.Loading);

        cancelablePromise(DeviceInfoService.deleteManufacturer(id))
            .then(() => {
                history.push('/manufacturers');
            })
            .catch((err) => {
                console.log('ERROR deleting: ', err);
                setDeleteState(LoadState.Error);
                setDeleteError(parseAxiosError(err));
            })
    }

    const requestDeleteModalClose = () => {
        // Ignore close requests if we're currently deleting
        if (deleteState !== LoadState.Loading) {
            setShowDeleteModal(false);
        }
    }

    const breadcrumbs = (
        <Breadcrumbs crumbs={[
            {
                label: t('manufacturersList.title'),
                url: '/manufacturers',
            },
        ]} 
        dataCy="edit_breadcrumb"
        />
    );

    let content = undefined;
    switch (loadState) {
        case LoadState.Initial:
        case LoadState.Loading:
            content = <Loader />;
            break;
        case LoadState.Success: {

            content = (<>
                {saveState === LoadState.Error && (
                    <ErrorMessage type={ErrorType.Generic} message={saveError} />
                )}
                <p>
                    Please enter your manufacturer.
                </p>
                <form onSubmit={form.handleSubmit(onSubmit)} data-cy="edit_form">
                    <div className="row">
                        <div className="col">
                            <FormInput
                                disabled={isAPICall}
                                form={form}
                                label={t('editManufacturer.fields.manufacturer')}
                                maxLength={128}
                                name="manufacturer"
                                registerOptions={{required: true}}
                                dataCy="edit_manufacturerInput"
                            />
                        </div>
                    </div>
                    {loadState === LoadState.Success && (
                        <div className="formFooter">
                            {isNew && (
                                <Button theme="text" type="button" disabled={isAPICall} onClick={onCancel} dataCy="edit_cancelButton">
                                    {t('button.cancel')}
                                </Button>
                            )}
                            {!isNew && (
                                <Button theme="text" type="button" disabled={isAPICall} onClick={onOpenDeleteModal} dataCy="edit_deleteButton">
                                    {t('button.delete')}
                                </Button>
                            )}
                            <Button theme="red" type="submit" disabled={isAPICall} dataCy="edit_submitButton">
                                {saveState === LoadState.Loading
                                    ? t('button.saving')
                                    : t('button.save')}
                            </Button>
                        </div>
                    )}
                </form>
            </>);
            break;
        }
        case LoadState.Error:
            content = <ErrorMessage type={ErrorType.Generic} message={loadError} dataCy="edit_loadError"/>
            break;
    }

    return (
        <Page>
            <PageHeader
                top={breadcrumbs}
                title={isNew ? t('editManufacturer.newTitle') : t('editManufacturer.title')}
            />
            {content}
            <Modal show={showDeleteModal} onHide={requestDeleteModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('editManufacturer.deleteModal.header')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteState === LoadState.Error && (
                        <ErrorMessage type={ErrorType.Generic} message={deleteError} />
                    )}
                    {t('editManufacturer.deleteModal.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button theme="text" onClick={requestDeleteModalClose} disabled={isAPICall} dataCy="deleteModal_cancelButton">
                        {t('button.cancel')}
                    </Button>
                    <Button theme="red" onClick={onDelete} disabled={isAPICall} dataCy="deleteModal_confirmButton">
                        {deleteState === LoadState.Loading
                            ? t('button.deleting')
                            : t('button.delete')
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
        </Page>
    );
}